// Office "Coverage zones" page (Session ADQ, admin only). A zone decides how quickly ELE aims to
// attend a call-out at a given postcode. A site is placed in a zone by the first part of its
// postcode (e.g. E17), the same mechanism the Director already approved for the congestion and
// parking surcharges: never a district prefix, never a polygon, never a geocoded distance.
//
// Admin only, deliberately: a response target is a commercial commitment ELE makes to a customer,
// not day-to-day office data, so it sits behind the same gate as the negotiated call-out rates.
//
// The server owns every rule (duplicate codes, one fallback per tenant, an outward code claimed by
// two zones, what a valid postcode area looks like). Its refusals are already plain English, so this
// page shows them VERBATIM rather than inventing its own wording that could drift from the rule.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import type { FormEvent } from "react";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import type { CoverageZone, CoverageZoneInput } from "../../lib/api";
import { ModalShell, MODAL_ACTION_BTN, MODAL_ACTIONS } from "../../components/ModalShell";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { AccessRestricted } from "../../components/AccessRestricted";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { ListToolbar } from "../../components/ListToolbar";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { SortableHeader } from "../../components/SortableHeader";
import { StatusPill } from "../../components/StatusPill";
import { filterRows, useListSearch, type SearchAccessors } from "../../lib/useListSearch";
import { sortRows, useSortableColumn, type SortAccessors } from "../../lib/useSortableColumn";
import {
  formatOutwardCodes,
  formatSlaMinutes,
  formatSlaTarget,
  parseOutwardCodes,
  slaApiErrorText,
} from "../../lib/slaFormat";

interface FormState {
  code: string;
  name: string;
  outwardCodes: string;
  attendanceTargetMins: string;
  fixTargetMins: string;
  entrapmentAttendanceTargetMins: string;
  isFallback: boolean;
  sortOrder: string;
  notes: string;
}

const EMPTY_FORM: FormState = {
  code: "",
  name: "",
  outwardCodes: "",
  attendanceTargetMins: "",
  fixTargetMins: "",
  entrapmentAttendanceTargetMins: "",
  isFallback: false,
  sortOrder: "",
  notes: "",
};

function formFromZone(z: CoverageZone): FormState {
  return {
    code: z.code,
    name: z.name,
    outwardCodes: formatOutwardCodes(z.outwardCodes),
    attendanceTargetMins: String(z.attendanceTargetMins),
    fixTargetMins: z.fixTargetMins == null ? "" : String(z.fixTargetMins),
    entrapmentAttendanceTargetMins:
      z.entrapmentAttendanceTargetMins == null ? "" : String(z.entrapmentAttendanceTargetMins),
    isFallback: z.isFallback,
    sortOrder: String(z.sortOrder),
    notes: z.notes ?? "",
  };
}

// A trimmed minutes string -> whole minutes, null when blank (clear the target), or "invalid".
// The server enforces the real bounds (1 to 10080); this only catches what is not a number at all,
// so the office never sees a request fail for something the form could have said itself.
function minsOrNull(s: string): number | null | "invalid" {
  return wholeOrNull(s, 1);
}

// The list order shares the parser but allows 0 (the server's floor for sortOrder is 0, not 1).
function sortOrderOrNull(s: string): number | null | "invalid" {
  return wholeOrNull(s, 0);
}

function wholeOrNull(s: string, min: number): number | null | "invalid" {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < min) return "invalid";
  return n;
}

const INPUT_FIELD =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field-inset px-3 py-1.5 text-sm text-ele-strong outline-none focus:border-ele-green";
const LABEL_TEXT = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

const ZONE_SEARCH: SearchAccessors<CoverageZone> = [
  (z) => z.name,
  (z) => z.code,
  (z) => z.outwardCodes.join(" "),
];
const ZONE_SORT: SortAccessors<CoverageZone> = {
  name: (z) => z.name.toLowerCase(),
  code: (z) => z.code.toLowerCase(),
  attend: (z) => z.attendanceTargetMins,
  fix: (z) => z.fixTargetMins,
};
const SORT_OPTIONS = [
  { key: "name", label: "Zone" },
  { key: "code", label: "Code" },
  { key: "attend", label: "Attend within" },
  { key: "fix", label: "Fix within" },
];

export function CoverageZonesPage() {
  const { user } = useAuth();
  const [zones, setZones] = useState<CoverageZone[]>([]);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [seeding, setSeeding] = useState(false);
  const [seedError, setSeedError] = useState<string | null>(null);

  // null = closed; { zone: null } = add; { zone } = edit.
  const [editor, setEditor] = useState<{ zone: CoverageZone | null } | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [confirmDelete, setConfirmDelete] = useState<CoverageZone | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState("loading");
    try {
      const res = await api.sla.zones();
      setZones(res.zones);
      setState("ready");
    } catch {
      setState("error");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const { search, setSearch } = useListSearch();
  const sort = useSortableColumn("coverageZones");
  const visible = useMemo(
    () => sortRows(filterRows(zones, search, ZONE_SEARCH), ZONE_SORT, sort),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [zones, search, sort.sortKey, sort.sortDir],
  );

  // Admin only. SUPERSEDES the earlier silent redirect (Session AQT, P-2): a bounce back to the
  // dashboard with no explanation reads as a bug, not a boundary, and the nav no longer even
  // offers this link to a non-admin (P-1) -- this now only fires for a stale bookmark or a typed
  // URL, and says plainly why. Waits for the user to load so a refresh straight onto this URL
  // does not show the message to an admin before their session resolves.
  if (user && user.role !== "admin") {
    return (
      <div className="mx-auto max-w-4xl px-4 py-6">
        <AccessRestricted message="Coverage zones are only available to admin accounts." />
      </div>
    );
  }

  function startAdd() {
    setForm(EMPTY_FORM);
    setFormError(null);
    setEditor({ zone: null });
  }

  function startEdit(zone: CoverageZone) {
    setForm(formFromZone(zone));
    setFormError(null);
    setEditor({ zone });
  }

  async function seed() {
    setSeeding(true);
    setSeedError(null);
    try {
      const res = await api.sla.seedZones();
      setZones(res.zones);
      setState("ready");
    } catch (err) {
      setSeedError(slaApiErrorText(err, "Could not set up the starting zones. Please try again."));
    } finally {
      setSeeding(false);
    }
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!editor) return;

    const attendance = minsOrNull(form.attendanceTargetMins);
    const fix = minsOrNull(form.fixTargetMins);
    const entrapment = minsOrNull(form.entrapmentAttendanceTargetMins);
    const sort = sortOrderOrNull(form.sortOrder);

    if (attendance === "invalid" || attendance == null) {
      setFormError("Enter the attendance target as a whole number of minutes, for example 60.");
      return;
    }
    if (fix === "invalid" || entrapment === "invalid") {
      setFormError("Enter each target as a whole number of minutes, or leave it blank for no target.");
      return;
    }
    if (sort === "invalid") {
      setFormError("Enter the list order as a whole number, for example 1.");
      return;
    }
    if (form.code.trim() === "" || form.name.trim() === "") {
      setFormError("Give the zone a short code (for example LOCAL) and a name (for example Local).");
      return;
    }

    const body: CoverageZoneInput = {
      code: form.code.trim(),
      name: form.name.trim(),
      outwardCodes: parseOutwardCodes(form.outwardCodes),
      attendanceTargetMins: attendance,
      fixTargetMins: fix,
      entrapmentAttendanceTargetMins: entrapment,
      isFallback: form.isFallback,
      notes: form.notes.trim() === "" ? null : form.notes.trim(),
    };
    // Omitted rather than sent as 0: an untouched list order on a new zone should take the database
    // default, not be pinned to the top.
    if (sort != null) body.sortOrder = sort;

    setSaving(true);
    setFormError(null);
    try {
      if (editor.zone) {
        const updated = await api.sla.updateZone(editor.zone.id, body);
        setZones((xs) => xs.map((x) => (x.id === updated.id ? updated : x)));
      } else {
        await api.sla.createZone(body);
      }
      setEditor(null);
      // Re-read rather than splice: a create lands in the server's own order (sortOrder then code),
      // and setting a new fallback can change another zone's meaning, so the list is refetched.
      await load();
    } catch (err) {
      // The server's 409s (zone_exists, fallback_exists, outward_code_claimed) and its 400 field
      // messages are already plain English. Show them as they are.
      setFormError(slaApiErrorText(err, "Could not save the zone. Please try again."));
    } finally {
      setSaving(false);
    }
  }

  async function doDelete() {
    if (!confirmDelete) return;
    const target = confirmDelete;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.sla.removeZone(target.id);
      setZones((xs) => xs.filter((x) => x.id !== target.id));
      setConfirmDelete(null);
    } catch (err) {
      setDeleteError(slaApiErrorText(err, "Could not delete the zone. Please try again."));
    } finally {
      setDeleting(false);
    }
  }

  const zoneToolbar = (
    <ListToolbar
      className="mb-3"
      search={search}
      onSearchChange={setSearch}
      searchPlaceholder="Search zone, code or postcode area..."
      searchLabel="Search coverage zones"
      resultCount={{ shown: visible.length, total: zones.length, noun: "zones" }}
      sort={{ state: sort, options: SORT_OPTIONS, onChange: (st) => sort.setSort(st.sortKey, st.sortDir) }}
      onClear={() => setSearch("")}
    />
  );

  return (
    <div>
      <PageHeader
        className="mb-4"
        title="Coverage zones"
        subtitle="A zone decides how quickly ELE aims to attend a call-out at that postcode. A site is matched to a zone by the first part of its postcode, for example E17. Every site in the zone is then measured against the targets you set here."
        actions={
          state === "ready" && zones.length > 0 ? (
            <Button icon={<Plus aria-hidden size={15} />} onClick={startAdd}>
              Add a zone
            </Button>
          ) : undefined
        }
      />

      {state === "loading" ? (
        <Loader label="Loading the coverage zones..." />
      ) : state === "error" ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the coverage zones. Please try again in a moment.
        </p>
      ) : zones.length === 0 ? (
        <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6">
          <h2 className="font-heading text-lg text-ele-blue">No coverage zones yet</h2>
          <p className="mt-2 max-w-3xl text-sm text-ele-body">
            Until zones are set up, every site is measured against ELE's published promise to arrive
            where possible within one hour of contact.
          </p>
          {seedError && (
            <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {seedError}
            </p>
          )}
          <div className="mt-4 flex flex-wrap gap-3">
            <Button onClick={seed} disabled={seeding}>
              {seeding ? "Setting up..." : "Set up the three starting zones"}
            </Button>
            <Button variant="secondary" onClick={startAdd}>
              Add a zone myself
            </Button>
          </div>
        </div>
      ) : visible.length === 0 ? (
        <>
          {zoneToolbar}
          <EmptyState role="status" message={`No coverage zones match "${search.trim()}".`} />
        </>
      ) : (
        <>
        {zoneToolbar}
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            {/* Below md the budget is the zone (the identifier), its attendance target (the point of
                the page) and the actions. The code, postcode areas and fix target drop. */}
            <colgroup>
              <col style={{ width: "22%" }} />
              <col className="max-md:hidden" style={{ width: "12%" }} />
              <col className="max-md:hidden" style={{ width: "26%" }} />
              <col style={{ width: "16%" }} />
              <col className="max-md:hidden" style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <SortableHeader label="Zone" colKey="name" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Code" colKey="code" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <th className="max-md:hidden px-3 py-2">Postcode areas</th>
                <SortableHeader label="Attend within" colKey="attend" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Fix within" colKey="fix" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <th className="cell-actions px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((z) => (
                <tr key={z.id} className="border-t border-ele-purple-accent">
                  <td className="px-3 py-2 align-top">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-ele-strong">{z.name}</span>
                      {z.isFallback && (
                        <StatusPill word="Fallback" tone="purple" title="Used for any postcode not listed in another zone" />
                      )}
                    </div>
                  </td>
                  <td className="max-md:hidden px-3 py-2 align-top font-menu text-xs uppercase tracking-wide text-ele-blue">
                    {z.code}
                  </td>
                  {/* cell-wrap + break-words (the Session ACX pattern): a long postcode list wraps
                      and grows the row instead of spilling over the next column. */}
                  <td className="cell-wrap break-words px-3 py-2 align-top text-ele-body max-md:hidden">
                    {z.outwardCodes.length > 0 ? (
                      formatOutwardCodes(z.outwardCodes)
                    ) : (
                      <span className="text-ele-body opacity-70">
                        {z.isFallback ? "Any postcode not listed elsewhere" : "None yet, so this zone applies to nothing"}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top text-ele-strong">{formatSlaMinutes(z.attendanceTargetMins)}</td>
                  <td className="max-md:hidden px-3 py-2 align-top text-ele-body">{formatSlaTarget(z.fixTargetMins)}</td>
                  <td className="cell-actions px-3 py-2 text-right align-top">
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" size="sm" onClick={() => startEdit(z)}>
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          setDeleteError(null);
                          setConfirmDelete(z);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {editor && (
        <ModalShell
          ariaLabel={editor.zone ? `Edit the ${editor.zone.name} zone` : "Add a coverage zone"}
          onClose={() => !saving && setEditor(null)}
          width="max-w-[560px]"
          panelClassName="p-6"
        >
          <form onSubmit={save}>
            <h2 className="mb-1 font-heading text-xl text-ele-strong">
              {editor.zone ? `Edit ${editor.zone.name}` : "Add a coverage zone"}
            </h2>
            <p className="mb-4 text-xs text-ele-body">
              Targets are in minutes from the moment the call is reported.
            </p>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className={LABEL_TEXT}>Code</span>
                <input
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                  placeholder="LOCAL"
                  className={INPUT_FIELD}
                />
              </label>
              <label className="block">
                <span className={LABEL_TEXT}>Name</span>
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Local"
                  className={INPUT_FIELD}
                />
              </label>
            </div>

            <label className="mt-3 block">
              <span className={LABEL_TEXT}>Postcode areas</span>
              <input
                value={form.outwardCodes}
                onChange={(e) => setForm((f) => ({ ...f, outwardCodes: e.target.value }))}
                placeholder="E17, E10, N15"
                className={INPUT_FIELD}
              />
              <span className="mt-1 block text-xs text-ele-body">
                The first part of a postcode only, separated by commas or spaces. A postcode area can
                belong to one zone only.
              </span>
            </label>

            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className={LABEL_TEXT}>Attend within (minutes)</span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  value={form.attendanceTargetMins}
                  onChange={(e) => setForm((f) => ({ ...f, attendanceTargetMins: e.target.value }))}
                  placeholder="60"
                  className={INPUT_FIELD}
                />
              </label>
              <label className="block">
                <span className={LABEL_TEXT}>Fix within (minutes, optional)</span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  value={form.fixTargetMins}
                  onChange={(e) => setForm((f) => ({ ...f, fixTargetMins: e.target.value }))}
                  placeholder="No target"
                  className={INPUT_FIELD}
                />
                <span className="mt-1 block text-xs text-ele-body">
                  ELE currently publishes a response (arrival) promise only, not a separate fix or
                  repair-time promise, so this is usually left blank. It measures the whole job, from
                  the report to the engineer leaving, not just the time after arrival: only fill it in
                  if there is a genuine, separate commitment to finish the work by.
                </span>
              </label>
              <label className="block">
                <span className={LABEL_TEXT}>Entrapment: attend within (minutes, optional)</span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  value={form.entrapmentAttendanceTargetMins}
                  onChange={(e) => setForm((f) => ({ ...f, entrapmentAttendanceTargetMins: e.target.value }))}
                  placeholder="60"
                  className={INPUT_FIELD}
                />
              </label>
              <label className="block">
                <span className={LABEL_TEXT}>List order (optional)</span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  inputMode="numeric"
                  value={form.sortOrder}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
                  placeholder="1"
                  className={INPUT_FIELD}
                />
              </label>
            </div>

            <label className="mt-3 flex items-start gap-2">
              <input
                type="checkbox"
                checked={form.isFallback}
                onChange={(e) => setForm((f) => ({ ...f, isFallback: e.target.checked }))}
                className="mt-0.5"
              />
              <span className="text-sm text-ele-body">
                Use this zone for any postcode not listed in another zone. Only one zone can do this.
              </span>
            </label>

            <label className="mt-3 block">
              <span className={LABEL_TEXT}>Notes (optional)</span>
              <textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="For example why this target was agreed."
                className={INPUT_FIELD}
              />
            </label>

            {formError && (
              <p role="alert" className="mt-4 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
                {formError}
              </p>
            )}

            <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
              <Button variant="ghost" className={MODAL_ACTION_BTN} onClick={() => setEditor(null)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" className={MODAL_ACTION_BTN} disabled={saving}>
                {saving ? "Saving..." : editor.zone ? "Save changes" : "Add zone"}
              </Button>
            </div>
          </form>
        </ModalShell>
      )}

      <ConfirmDeleteModal
        open={confirmDelete != null}
        title={confirmDelete ? `Delete the ${confirmDelete.name} zone?` : ""}
        message="Sites in this zone will be measured against another zone, or against ELE's published one-hour promise if no other zone covers their postcode. Call-outs already recorded keep the target they were measured against."
        warning={
          confirmDelete?.isFallback
            ? "This is the fallback zone. Once it is gone, any postcode not listed in another zone is measured against the one-hour promise."
            : null
        }
        confirmLabel="Delete zone"
        variant="destructive"
        busy={deleting}
        error={deleteError}
        onConfirm={doDelete}
        onCancel={() => {
          setConfirmDelete(null);
          setDeleteError(null);
        }}
      />
    </div>
  );
}
